import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { rolesCanManageTeamAccess, PermissionCheckFn } from '@core/auth/team-access';
import { NAV_MODULE_PERMISSION } from '@core/auth/nav-permissions.config';
import {
  FINANCE_NAV_MODULE_ID,
  HOME_NAV_MODULE_ID,
  INVENTORY_NAV_MODULE_ID,
  NAV_MODULE_GROUP_RESOURCES,
  navRoutePermission,
  navRoutePermissionAnyOf,
  PAYROLL_NAV_MODULE_ID,
  PLACEHOLDER_NAV_MODULE_IDS,
  PLACEHOLDER_NAV_ROUTES,
  PURCHASES_NAV_MODULE_ID,
  REPORTS_NAV_MODULE_ID,
} from '@core/auth/nav-route-permissions.config';
import { ADMIN_SHELL_NAV_ICONS } from '@core/shell/admin-shell-nav-icons';
import {
  AdminShellMode,
  CONFIG_NAV_MODULE_ID,
  PLATFORM_NAV_MODULE_ID,
  POS_RAIL_CARRITOS_ID,
  POS_RAIL_CONFIG_ID,
  POS_RAIL_EXIT_ID,
  POS_RAIL_INFORME_ID,
  POS_RAIL_MESAS_ID,
  POS_RAIL_TABLERO_ID,
  POS_RAIL_TURNOS_ID,
  POS_RAIL_VENTA_ID,
} from '@core/shell/admin-shell-mode.model';
import { NavItem } from './nav-item/nav-item';

export {
  PLATFORM_NAV_MODULE_ID,
  POS_NAV_MODULE_ID,
  POS_RAIL_VENTA_ID,
  CONFIG_NAV_MODULE_ID,
} from '@core/shell/admin-shell-mode.model';

export interface StorefrontModuleGate {
  readonly catalogEnabled: boolean;
  readonly linksEnabled: boolean;
  readonly servicesEnabled: boolean;
}

/**
 * Techo comercial del plan (entitlements). Nombres alineados al rail / storefront.
 * Se combina con StorefrontModuleGate (activación por sede): plan AND sede.
 */
export interface PlanModuleGate {
  /** Rail Mi página completo. */
  readonly moduleMyPage: boolean;
  readonly moduleLinks: boolean;
  readonly moduleCatalog: boolean;
  /** Catálogo → Productos (requiere moduleCatalog). */
  readonly moduleProducts: boolean;
  /** Catálogo → Categorías. */
  readonly moduleCategories: boolean;
  /** Catálogo → Atributos. */
  readonly moduleAttributes: boolean;
  /** Catálogo → Modificadores (`allow_modifiers`). */
  readonly allowModifiers: boolean;
  /** Catálogo → Promociones. */
  readonly modulePromotions: boolean;
  /** Rail Clientes. */
  readonly moduleCustomers: boolean;
  /** Rail Servicios. */
  readonly moduleServices: boolean;
  /** Rail Inventario (padre). */
  readonly moduleInventory: boolean;
  /** Inventario → Stock. */
  readonly moduleInventoryStock: boolean;
  /** Inventario → Movimientos. */
  readonly moduleInventoryMovements: boolean;
  /** Inventario → Transferencias. */
  readonly moduleInventoryTransfers: boolean;
  /** Inventario → Almacenes. */
  readonly moduleInventoryWarehouses: boolean;
  /** Rail Finanzas. */
  readonly moduleFinance: boolean;
  /** Finanzas → Ingresos y gastos. */
  readonly moduleFinanceTransactions: boolean;
  /** Finanzas → Cuentas. */
  readonly moduleFinanceAccounts: boolean;
  /** Gema: Contador de efectivo. */
  readonly moduleCashCounter: boolean;
  /** Finanzas → Créditos. */
  readonly moduleFinanceCredits: boolean;
  /** Rail Compras (proveedores, insumos). */
  readonly modulePurchases: boolean;
  /** Compras → Órdenes. */
  readonly modulePurchasesOrders: boolean;
  /** Compras → Proveedores. */
  readonly modulePurchasesProviders: boolean;
  /** Compras → Insumos. */
  readonly modulePurchasesSupplies: boolean;
  /** Rail Nómina (empleados y propinas). */
  readonly modulePayroll: boolean;
  /** Módulo Configuración (empresa, usuarios, roles…). */
  readonly moduleConfiguration: boolean;
  /** Configuración → Usuarios (requiere moduleConfiguration). */
  readonly moduleUsers: boolean;
  /** Configuración → Sedes (flag; ruta admin puede no existir aún). */
  readonly moduleLocations: boolean;
  readonly moduleSales: boolean;
  readonly moduleSalesOrders: boolean;
  readonly modulePos: boolean;
  readonly modulePosTables: boolean;
  readonly salesBoard: boolean;
}

export interface NavBuildOptions {
  readonly can?: PermissionCheckFn;
  /** Flags de módulo de la tienda; ocultan rutas del storefront cuando el módulo está apagado. */
  readonly storefrontModules?: StorefrontModuleGate;
  /** Techo del plan; si está ausente (plan aún no cargado), no filtra por plan. */
  readonly planModules?: PlanModuleGate;
  /** Plan: oculta admin de roles si roles_admin = false. */
  readonly rolesAdmin?: boolean;
  /** Sin requireCashShift: oculta el rail/ítem de Turnos de caja. */
  readonly hidePosTurnos?: boolean;
  /** Setup Mi página completo: oculta Checklist (/starter); la ruta sigue existiendo. */
  readonly hideMyPageChecklist?: boolean;
}

/**
 * Ruta del storefront → módulo(s) que la habilitan. Si es una lista, basta con que
 * uno esté activo. Contacto (`/storefront/store`) no está aquí: siempre con Mi página.
 */
const STOREFRONT_ROUTE_MODULE: Readonly<
  Record<string, keyof StorefrontModuleGate | ReadonlyArray<keyof StorefrontModuleGate>>
> = {
  '/storefront/enlaces': 'linksEnabled',
  // Contacto y horario: parte de Mi página (presencia), no exige catálogo/servicios.
  '/storefront/catalogo': 'catalogEnabled',
  '/storefront/entrega': 'catalogEnabled',
  '/storefront/pagos': 'catalogEnabled',
  // Servicios (CRUD) y solicitudes: techo module_services + sede con servicios activos.
  '/catalog/services': 'servicesEnabled',
  '/sales/requests': 'servicesEnabled',
};

/** Combina techo del plan + activación de sede. */
export function effectiveStorefrontModules(
  location?: StorefrontModuleGate,
  plan?: PlanModuleGate
): StorefrontModuleGate | undefined {
  if (!location && !plan) {
    return undefined;
  }
  const loc = location ?? {
    catalogEnabled: true,
    linksEnabled: true,
    // No asumir servicios on: evita Solicitudes/CRUD visibles hasta hidratar la sede.
    servicesEnabled: false,
  };
  const p = plan ?? {
    moduleMyPage: true,
    moduleLinks: true,
    moduleCatalog: true,
    moduleProducts: true,
    moduleCategories: true,
    moduleAttributes: true,
    allowModifiers: true,
    modulePromotions: true,
    moduleCustomers: true,
    moduleServices: true,
    moduleInventory: true,
    moduleInventoryStock: true,
    moduleInventoryMovements: true,
    moduleInventoryTransfers: true,
    moduleInventoryWarehouses: true,
    moduleFinance: true,
    moduleFinanceTransactions: true,
    moduleFinanceAccounts: true,
    moduleCashCounter: true,
    moduleFinanceCredits: true,
    modulePurchases: true,
    modulePurchasesOrders: true,
    modulePurchasesProviders: true,
    modulePurchasesSupplies: true,
    modulePayroll: true,
    moduleConfiguration: true,
    moduleUsers: true,
    moduleLocations: true,
    moduleSales: true,
    moduleSalesOrders: true,
    modulePos: true,
    modulePosTables: true,
    salesBoard: true,
  };
  return {
    linksEnabled: loc.linksEnabled && p.moduleLinks && p.moduleMyPage,
    catalogEnabled: loc.catalogEnabled && p.moduleCatalog && p.moduleMyPage,
    servicesEnabled: loc.servicesEnabled && p.moduleServices && p.moduleMyPage,
  };
}

function routePassesModuleGate(path: string, modules?: StorefrontModuleGate): boolean {
  if (!modules) {
    return true;
  }
  // Prefijos de módulos sede (hijos /new, /:id/edit, etc.).
  if (path.startsWith('/catalog/services')) {
    return modules.servicesEnabled;
  }
  if (path.startsWith('/sales/requests')) {
    return modules.servicesEnabled;
  }
  const required = STOREFRONT_ROUTE_MODULE[path];
  if (!required) {
    return true;
  }
  const requiredModules: ReadonlyArray<keyof StorefrontModuleGate> = Array.isArray(required)
    ? required
    : [required];
  return requiredModules.some((module) => modules[module]);
}

export function routePassesPlanGate(path: string, plan?: PlanModuleGate): boolean {
  if (!plan) {
    return true;
  }
  if (
    path === '/starter' ||
    path.startsWith('/storefront/') ||
    path.startsWith('/onboard')
  ) {
    return plan.moduleMyPage;
  }
  if (path.startsWith('/catalog')) {
    if (path.startsWith('/catalog/services')) {
      return plan.moduleServices;
    }
    if (!plan.moduleCatalog) {
      return false;
    }
    // Root visible con el módulo padre; hijos con su submódulo.
    if (path === '/catalog') {
      return true;
    }
    if (path.startsWith('/catalog/products')) {
      return plan.moduleProducts;
    }
    if (path.startsWith('/catalog/categories')) {
      return plan.moduleCategories;
    }
    if (path.startsWith('/catalog/attributes')) {
      return plan.moduleAttributes;
    }
    if (path.startsWith('/catalog/modifiers')) {
      return plan.allowModifiers;
    }
    if (path.startsWith('/catalog/promotions')) {
      return plan.modulePromotions;
    }
    return true;
  }
  if (path.startsWith('/customers')) {
    return plan.moduleCustomers;
  }
  if (path.startsWith('/inventory')) {
    if (!plan.moduleInventory) {
      return false;
    }
    if (path.startsWith('/inventory/stock') || path.startsWith('/inventory/adjustments') || path.startsWith('/inventory/physical-count')) {
      return plan.moduleInventoryStock;
    }
    if (path.startsWith('/inventory/movements')) {
      return plan.moduleInventoryMovements;
    }
    if (path.startsWith('/inventory/transfers')) {
      return plan.moduleInventoryTransfers;
    }
    if (path.startsWith('/inventory/warehouses')) {
      return plan.moduleInventoryWarehouses;
    }
    return true;
  }
  if (path.startsWith('/finance')) {
    if (!plan.moduleFinance) {
      return false;
    }
    if (
      path.startsWith('/finance/income-expenses') ||
      path.startsWith('/finance/categories') ||
      path.startsWith('/finance/income') ||
      path.startsWith('/finance/expenses')
    ) {
      return plan.moduleFinanceTransactions;
    }
    if (
      path.startsWith('/finance/accounts') ||
      path.startsWith('/finance/transfers')
    ) {
      return plan.moduleFinanceAccounts;
    }
    if (path.startsWith('/finance/cash-settings')) {
      return plan.moduleFinanceAccounts && plan.modulePos;
    }
    if (path.startsWith('/finance/cash-counter')) {
      return plan.moduleFinanceAccounts && plan.moduleCashCounter;
    }
    if (path.startsWith('/finance/configuracion')) {
      return plan.moduleFinanceAccounts || plan.moduleFinanceTransactions;
    }
    if (path.startsWith('/finance/credits')) {
      return plan.moduleFinanceCredits;
    }
    return true;
  }
  if (path.startsWith('/purchases')) {
    if (!plan.modulePurchases) {
      return false;
    }
    if (path.startsWith('/purchases/orders')) {
      return plan.modulePurchasesOrders;
    }
    if (path.startsWith('/purchases/providers')) {
      return plan.modulePurchasesProviders;
    }
    if (path.startsWith('/purchases/supplies')) {
      return plan.modulePurchasesSupplies;
    }
    return true;
  }
  if (path.startsWith('/payroll')) {
    return plan.modulePayroll;
  }
  if (path.startsWith('/reports/propinas')) {
    return plan.modulePayroll;
  }
  // Legacy tip URLs (redirect → /payroll|/reports).
  if (path.startsWith('/sales/tip-')) {
    return plan.modulePayroll;
  }
  if (path.startsWith('/sales/orders')) {
    if (!plan.moduleSales) {
      return false;
    }
    return plan.moduleSalesOrders;
  }
  if (path.startsWith('/sales/configuracion')) {
    if (!plan.moduleSales) {
      return false;
    }
    return plan.moduleSalesOrders;
  }
  if (path === '/sales/board' || path === '/pos/tablero' || path === '/sales/pos/tablero') {
    if (!plan.moduleSales) {
      return false;
    }
    return plan.salesBoard;
  }
  if (
    path === '/pos/mesas' ||
    path.startsWith('/pos/mesas/') ||
    path === '/sales/pos/mesas' ||
    path.startsWith('/sales/pos/mesas/')
  ) {
    if (!plan.moduleSales || !plan.modulePos) {
      return false;
    }
    return plan.modulePosTables;
  }
  if (path.startsWith('/pos/') || path.startsWith('/sales/pos/')) {
    if (!plan.moduleSales) {
      return false;
    }
    return plan.modulePos;
  }
  if (path.startsWith('/sales/requests')) {
    return plan.moduleServices;
  }
  // Reportes: Link/Free no los incluye; Vender+ según módulo.
  if (path === '/reports' || path.startsWith('/reports/resumen')) {
    return plan.moduleSalesOrders || plan.modulePos || plan.moduleCatalog;
  }
  if (path.startsWith('/reports/ventas') || path.startsWith('/reports/productos')) {
    return plan.moduleCatalog || plan.modulePos || plan.moduleSalesOrders;
  }
  if (path.startsWith('/reports/ingresos-egresos')) {
    return plan.moduleFinance;
  }
  if (path.startsWith('/reports/egresos-proveedor')) {
    return plan.modulePurchases || plan.moduleFinance;
  }
  if (path.startsWith('/reports/domicilios')) {
    return plan.moduleSales || plan.moduleCatalog;
  }
  if (path.startsWith('/reports/')) {
    return plan.moduleSalesOrders || plan.modulePos || plan.moduleCatalog || plan.moduleFinance;
  }
  if (path.startsWith('/settings/') || path.startsWith('/account/')) {
    if (!plan.moduleConfiguration) {
      return false;
    }
    if (path.startsWith('/account/users')) {
      return plan.moduleUsers;
    }
    // Sin ruta de sedes en nav hoy; el flag queda listo para UI futura.
    if (path.startsWith('/settings/locations') || path.startsWith('/account/locations')) {
      return plan.moduleLocations;
    }
    return true;
  }
  return true;
}

function railModulePassesPlanGate(moduleId: number, plan?: PlanModuleGate): boolean {
  if (!plan) {
    return true;
  }
  // 2 = Mi página
  if (moduleId === 2) {
    return plan.moduleMyPage;
  }
  // 3 = Catálogo (productos y/o servicios)
  if (moduleId === 3) {
    return plan.moduleCatalog || plan.moduleServices;
  }
  // 4 = Ventas
  if (moduleId === 4) {
    return plan.moduleSales;
  }
  if (moduleId === POS_RAIL_TABLERO_ID) {
    return plan.salesBoard;
  }
  if (moduleId === POS_RAIL_MESAS_ID) {
    return plan.modulePosTables;
  }
  // 5 = Clientes
  if (moduleId === 5) {
    return plan.moduleCustomers;
  }
  // Inventario
  if (moduleId === INVENTORY_NAV_MODULE_ID) {
    return plan.moduleInventory;
  }
  if (moduleId === FINANCE_NAV_MODULE_ID) {
    return plan.moduleFinance;
  }
  if (moduleId === PURCHASES_NAV_MODULE_ID) {
    return plan.modulePurchases;
  }
  if (moduleId === PAYROLL_NAV_MODULE_ID) {
    return plan.modulePayroll;
  }
  if (moduleId === REPORTS_NAV_MODULE_ID) {
    // Link/Free: sin catálogo/pedidos/POS/finanzas/nómina → sin rail Reportes.
    return (
      plan.moduleSalesOrders ||
      plan.modulePos ||
      plan.moduleCatalog ||
      plan.moduleFinance ||
      plan.modulePayroll
    );
  }
  if (moduleId === CONFIG_NAV_MODULE_ID) {
    return plan.moduleConfiguration;
  }
  return true;
}

/** Visible en rail solo si el plan habilita el módulo (upsell va contextual, no en nav). */
function railModuleVisibleForPlan(moduleId: number, plan?: PlanModuleGate): boolean {
  return railModulePassesPlanGate(moduleId, plan);
}

function modulePassesPermission(
  moduleId: number,
  roles: string[],
  can?: PermissionCheckFn
): boolean {
  if (PLACEHOLDER_NAV_MODULE_IDS.has(moduleId)) {
    return false;
  }
  if (rolesIncludeSuperadmin(roles)) {
    return true;
  }
  if (moduleId === HOME_NAV_MODULE_ID) {
    return true;
  }
  const groupResources = NAV_MODULE_GROUP_RESOURCES[moduleId];
  if (groupResources && can) {
    // Solo lectura: create/update no deben abrir el rail (plantilla owner global).
    const groupAllowed = groupResources.some(
      (resource) => can(resource, 'index') || can(resource, 'show'),
    );
    if (groupAllowed) {
      return true;
    }
  }
  const requirement = NAV_MODULE_PERMISSION[moduleId];
  if (!requirement || !can) {
    return false;
  }
  return can(requirement.resource, requirement.action);
}

function childRouteVisible(
  route: string,
  can?: PermissionCheckFn,
  modules?: StorefrontModuleGate,
  rolesAdmin?: boolean,
  plan?: PlanModuleGate,
  hideMyPageChecklist?: boolean,
  hidePosTurnos?: boolean
): boolean {
  const path = route.split('?')[0];
  if (PLACEHOLDER_NAV_ROUTES.has(path)) {
    return false;
  }
  if (path === '/starter' && hideMyPageChecklist) {
    return false;
  }
  if (
    hidePosTurnos &&
    (path === '/pos/turnos' ||
      path.startsWith('/pos/turnos/') ||
      path === '/sales/pos/turnos' ||
      path.startsWith('/sales/pos/turnos/'))
  ) {
    return false;
  }
  // Plataforma: gated by shell mode + superadminOnly module, not tenant matrix.
  if (path.startsWith('/platform')) {
    return true;
  }
  // Salida del shell POS: siempre visible si el módulo POS ya pasó el filtro de permiso.
  if (path === '/pos/salir') {
    return true;
  }
  if (path === '/dashboard' || path.startsWith('/dashboard/')) {
    return !can || can('dashboard', 'index') || can('dashboard', 'show');
  }
  if (!routePassesPlanGate(path, plan)) {
    // Fuera de plan: ocultar del nav. Upsell contextual (Mi página, sheets), no chip "Plan".
    return false;
  }
  if (!routePassesModuleGate(path, modules)) {
    return false;
  }
  if (!can) {
    return false;
  }
  const anyOf = navRoutePermissionAnyOf(path);
  if (anyOf?.length) {
    return anyOf.some((req) => can(req.resource, req.action));
  }
  const requirement = navRoutePermission(path);
  if (!requirement) {
    return false;
  }
  return can(requirement.resource, requirement.action);
}

/**
 * Una sola definición por módulo (rail + panel lateral).
 * Así, si cambias el orden visual, los `id` del rail y las rutas hijas siguen alineados.
 */
interface NavModuleConfig {
  readonly id: number;
  readonly railTitle: string;
  readonly railIcon: string;
  readonly name: string;
  readonly children: NavItem[];
  /** Solo visible para rol global `superadmin`. */
  readonly superadminOnly?: boolean;
  /** Solo visible para administradores de equipo (owner/admin global o superadmin). */
  readonly teamAdminOnly?: boolean;
  /** Oculto del rail principal; se abre desde el pie (engranaje). */
  readonly railHidden?: boolean;
  /** Solo visible en modo shell `pos` (no aparece en rail tenant/platform). */
  readonly posModeOnly?: boolean;
}

/** Etiqueta del módulo escaparate en rail y menú (alineado a `catalog.myPage.title`). */
const MY_PAGE_MODULE_LABEL = 'Mi página';

const NAV_MODULES: readonly NavModuleConfig[] = [
  {
    id: 1,
    railTitle: 'Inicio',
    railIcon: 'solar:home-2-line-duotone',
    name: 'Dashboard',
    /** Destino directo: highlight en rail; Completa muestra logo sin lista de 1 ítem. */
    children: [
      {
        displayName: 'Inicio',
        iconName: 'solar:home-2-line-duotone',
        route: '/dashboard',
      },
    ],
  },
  {
    id: 2,
    railTitle: MY_PAGE_MODULE_LABEL,
    railIcon: ADMIN_SHELL_NAV_ICONS.businessRail,
    name: 'Storefront',
    children: [
      { navCap: MY_PAGE_MODULE_LABEL, navCapBrand: true },
      {
        displayName: 'Checklist',
        iconName: 'solar:checklist-minimalistic-line-duotone',
        route: '/starter',
      },
      {
        displayName: 'Perfil',
        iconName: 'solar:user-circle-line-duotone',
        route: '/storefront/information',
      },
      {
        displayName: 'Enlaces',
        iconName: 'solar:link-round-line-duotone',
        route: '/storefront/enlaces',
      },
      {
        displayName: 'Apariencia',
        iconName: 'solar:palette-line-duotone',
        route: '/storefront/appearance',
      },
      {
        displayName: 'Contacto y horario',
        iconName: 'solar:phone-rounded-line-duotone',
        route: '/storefront/store',
      },
      {
        displayName: 'Catálogo',
        iconName: 'solar:gallery-wide-line-duotone',
        route: '/storefront/catalogo',
      },
      {
        displayName: 'Entrega',
        iconName: 'solar:delivery-line-duotone',
        route: '/storefront/entrega',
      },
      {
        displayName: 'Pagos',
        iconName: 'solar:wallet-money-line-duotone',
        route: '/storefront/pagos',
      },
    ],
  },
  {
    id: 3,
    railTitle: 'Catálogo',
    // Precio / oferta = productos a la venta (≠ widget-add, ≠ caja de inventario).
    railIcon: 'solar:tag-price-line-duotone',
    name: 'Catalog',
    children: [
      { navCap: 'CATÁLOGO' },
      {
        displayName: 'Productos',
        iconName: 'solar:box-line-duotone',
        route: '/catalog/products',
      },
      {
        displayName: 'Servicios',
        iconName: 'solar:clipboard-list-line-duotone',
        route: '/catalog/services',
      },
      {
        displayName: 'Promociones',
        iconName: 'solar:ticket-sale-line-duotone',
        route: '/catalog/promotions',
      },
    ],
  },
  {
    id: 4,
    railTitle: 'Ventas',
    railIcon: 'solar:cart-large-2-line-duotone',
    name: 'Sales',
    children: [
      { navCap: 'VENTAS' },
      {
        displayName: 'sidebar.posNav.venta',
        iconName: ADMIN_SHELL_NAV_ICONS.posVenta,
        route: '/sales/pos/venta',
      },
      {
        displayName: 'sidebar.posNav.mesas',
        iconName: ADMIN_SHELL_NAV_ICONS.posMesas,
        route: '/sales/pos/mesas',
      },
      {
        displayName: 'sidebar.posNav.tablero',
        iconName: ADMIN_SHELL_NAV_ICONS.posTablero,
        route: '/sales/pos/tablero',
      },
      {
        displayName: 'Pedidos',
        iconName: 'solar:clipboard-list-line-duotone',
        route: '/sales/orders',
      },
      {
        displayName: 'Solicitudes',
        iconName: 'solar:inbox-line-duotone',
        route: '/sales/requests',
      },
      {
        displayName: 'Informes',
        iconName: ADMIN_SHELL_NAV_ICONS.posInforme,
        route: '/sales/pos/informe',
      },
      {
        displayName: 'Turnos',
        iconName: ADMIN_SHELL_NAV_ICONS.posTurnos,
        route: '/sales/pos/turnos',
      },
      {
        displayName: 'sidebar.posNav.config',
        iconName: ADMIN_SHELL_NAV_ICONS.posConfig,
        route: '/sales/configuracion',
      },
      {
        displayName: 'shell.openPosShort',
        iconName: 'tabler:external-link',
        route: '/pos/venta',
        openPosInNewTab: true,
      },
    ],
  },
  {
    id: 5,
    railTitle: 'Clientes',
    railIcon: 'solar:users-group-rounded-line-duotone',
    name: 'Customers',
    children: [
      { navCap: 'CLIENTES' },
      {
        displayName: 'Clientes',
        iconName: 'solar:users-group-rounded-line-duotone',
        route: '/customers',
      },
    ],
  },
  {
    id: INVENTORY_NAV_MODULE_ID,
    railTitle: 'Inventario',
    railIcon: 'solar:box-minimalistic-line-duotone',
    name: 'Inventory',
    children: [
      { navCap: 'INVENTARIO' },
      {
        displayName: 'Stock',
        iconName: 'solar:archive-line-duotone',
        route: '/inventory/stock',
      },
      {
        displayName: 'Conteo físico',
        iconName: 'solar:clipboard-check-line-duotone',
        route: '/inventory/physical-count',
      },
      {
        displayName: 'Movimientos',
        iconName: 'solar:history-line-duotone',
        route: '/inventory/movements',
      },
      {
        displayName: 'Transferencias',
        iconName: 'solar:transfer-horizontal-line-duotone',
        route: '/inventory/transfers',
      },
      {
        displayName: 'Almacenes',
        iconName: 'solar:home-2-line-duotone',
        route: '/inventory/warehouses',
      },
    ],
  },
  {
    id: FINANCE_NAV_MODULE_ID,
    railTitle: 'Finanzas',
    railIcon: 'solar:banknote-2-line-duotone',
    name: 'Finance',
    children: [
      { navCap: 'FINANZAS' },
      {
        displayName: 'Ingresos y gastos',
        iconName: 'solar:bill-list-line-duotone',
        route: '/finance/income-expenses',
      },
      {
        displayName: 'finance.nav.transfers',
        iconName: 'solar:transfer-horizontal-line-duotone',
        route: '/finance/transfers',
      },
      {
        displayName: 'Cuentas',
        iconName: 'solar:safe-2-line-duotone',
        route: '/finance/accounts',
      },
      {
        displayName: 'header.profile.menu.cashCounter',
        iconName: 'solar:wad-of-money-line-duotone',
        route: '/finance/cash-counter',
        /** Desktop: utility rail. Móvil: chip del taller Finanzas. */
        mobileDockOnly: true,
      },
      {
        displayName: 'Créditos',
        iconName: 'solar:hand-money-line-duotone',
        route: '/finance/credits',
      },
      {
        displayName: 'finance.nav.config',
        iconName: 'solar:settings-line-duotone',
        route: '/finance/configuracion',
      },
    ],
  },
  {
    id: PURCHASES_NAV_MODULE_ID,
    railTitle: 'Compras',
    // Distinto de Ventas (`cart-large-2`): bolsa + check = aprovisionamiento.
    railIcon: 'solar:bag-check-line-duotone',
    name: 'Purchases',
    children: [
      { navCap: 'COMPRAS' },
      {
        displayName: 'Órdenes de compra',
        iconName: 'solar:clipboard-list-line-duotone',
        route: '/purchases/orders',
      },
      {
        displayName: 'Proveedores',
        iconName: 'solar:shop-line-duotone',
        route: '/purchases/providers',
      },
      {
        displayName: 'Insumos',
        iconName: 'solar:bottle-line-duotone',
        route: '/purchases/supplies',
      },
    ],
  },
  {
    id: PAYROLL_NAV_MODULE_ID,
    railTitle: 'Nómina',
    // Distinto de Clientes (`users-group-rounded`): maletín = equipo / HR.
    railIcon: 'solar:case-round-minimalistic-line-duotone',
    name: 'Payroll',
    children: [
      { navCap: 'NÓMINA' },
      {
        displayName: 'Empleados',
        iconName: 'solar:user-id-line-duotone',
        route: '/payroll/employees',
      },
      {
        displayName: 'Cargos',
        iconName: 'solar:hand-money-line-duotone',
        route: '/payroll/cargos',
      },
      {
        displayName: 'Liquidaciones',
        iconName: 'solar:wallet-money-line-duotone',
        route: '/payroll/liquidaciones',
      },
    ],
  },
  {
    id: REPORTS_NAV_MODULE_ID,
    railTitle: 'Reportes',
    railIcon: 'solar:chart-2-line-duotone',
    name: 'Reports',
    children: [
      { navCap: 'REPORTES' },
      {
        displayName: 'Resumen del negocio',
        iconName: 'solar:widget-line-duotone',
        route: '/reports/resumen',
      },
      {
        displayName: 'Ventas',
        iconName: 'solar:chart-square-line-duotone',
        route: '/reports/ventas',
      },
      {
        displayName: 'Ingresos y gastos',
        iconName: 'solar:wad-of-money-line-duotone',
        route: '/reports/ingresos-egresos',
      },
      {
        displayName: 'Propinas',
        iconName: 'solar:hand-money-line-duotone',
        route: '/reports/propinas',
      },
    ],
  },
  {
    id: CONFIG_NAV_MODULE_ID,
    railTitle: 'Configuración',
    railIcon: 'solar:settings-line-duotone',
    name: 'Configuration',
    railHidden: true,
    children: [
      { navCap: 'EMPRESA' },
      {
        displayName: 'Datos de la empresa',
        iconName: 'solar:buildings-2-line-duotone',
        route: '/settings/company',
      },
      {
        displayName: 'Sedes',
        iconName: 'solar:shop-2-line-duotone',
        route: '/settings/locations',
      },
      { navCap: 'USUARIOS Y PERMISOS', divider: true, teamAdminOnly: true },
      {
        displayName: 'Usuarios',
        iconName: 'solar:user-id-line-duotone',
        route: '/account/users',
        teamAdminOnly: true,
      },
      {
        displayName: 'Roles y permisos',
        iconName: 'solar:shield-user-line-duotone',
        route: '/account/roles',
        teamAdminOnly: true,
      },
    ],
  },
  {
    id: PLATFORM_NAV_MODULE_ID,
    railTitle: 'Plataforma',
    railIcon: ADMIN_SHELL_NAV_ICONS.platformRail,
    name: 'Plataforma',
    superadminOnly: true,
    children: [
      { navCap: 'PLATAFORMA' },
      {
        displayName: 'Resumen',
        iconName: ADMIN_SHELL_NAV_ICONS.platformOverviewNav,
        route: '/platform',
      },
      {
        displayName: 'Leads',
        iconName: ADMIN_SHELL_NAV_ICONS.platformLeadsNav,
        route: '/platform/leads',
      },
      {
        displayName: 'Tenants',
        iconName: ADMIN_SHELL_NAV_ICONS.platformTenantsNav,
        route: '/platform/tenants',
      },
      {
        displayName: 'Planes',
        iconName: ADMIN_SHELL_NAV_ICONS.platformPlansNav,
        route: '/platform/plans',
      },
      {
        displayName: 'Imágenes del sistema',
        iconName: ADMIN_SHELL_NAV_ICONS.platformSystemImagesNav,
        route: '/platform/system-images',
      },
      {
        displayName: 'Legal',
        iconName: ADMIN_SHELL_NAV_ICONS.platformLegalNav,
        route: '/platform/legal',
      },
    ],
  },
  {
    id: POS_RAIL_VENTA_ID,
    railTitle: 'sidebar.posNav.venta',
    railIcon: ADMIN_SHELL_NAV_ICONS.posVenta,
    name: 'POS Venta',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.venta',
        iconName: ADMIN_SHELL_NAV_ICONS.posVenta,
        route: '/pos/venta',
      },
    ],
  },
  {
    id: POS_RAIL_MESAS_ID,
    railTitle: 'sidebar.posNav.mesas',
    railIcon: ADMIN_SHELL_NAV_ICONS.posMesas,
    name: 'POS Mesas',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.mesas',
        iconName: ADMIN_SHELL_NAV_ICONS.posMesas,
        route: '/pos/mesas',
      },
    ],
  },
  {
    id: POS_RAIL_TABLERO_ID,
    railTitle: 'sidebar.posNav.tablero',
    railIcon: ADMIN_SHELL_NAV_ICONS.posTablero,
    name: 'POS Tablero',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.tablero',
        iconName: ADMIN_SHELL_NAV_ICONS.posTablero,
        route: '/pos/tablero',
      },
    ],
  },
  {
    id: POS_RAIL_CARRITOS_ID,
    railTitle: 'sidebar.posNav.carritosShort',
    railIcon: ADMIN_SHELL_NAV_ICONS.posCarritos,
    name: 'POS Pedidos',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.carritos',
        iconName: ADMIN_SHELL_NAV_ICONS.posCarritos,
        route: '/pos/pedidos',
      },
    ],
  },
  {
    id: POS_RAIL_INFORME_ID,
    railTitle: 'sidebar.posNav.informeShort',
    railIcon: ADMIN_SHELL_NAV_ICONS.posInforme,
    name: 'POS Informe',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.informe',
        iconName: ADMIN_SHELL_NAV_ICONS.posInforme,
        route: '/pos/informe',
      },
    ],
  },
  {
    id: POS_RAIL_TURNOS_ID,
    railTitle: 'sidebar.posNav.turnosShort',
    railIcon: ADMIN_SHELL_NAV_ICONS.posTurnos,
    name: 'POS Turnos',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.turnosCaja',
        iconName: ADMIN_SHELL_NAV_ICONS.posTurnos,
        route: '/pos/turnos',
      },
    ],
  },
  {
    id: POS_RAIL_CONFIG_ID,
    railTitle: 'sidebar.posNav.configShort',
    railIcon: ADMIN_SHELL_NAV_ICONS.posConfig,
    name: 'POS Config',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.config',
        iconName: ADMIN_SHELL_NAV_ICONS.posConfig,
        route: '/pos/configuracion',
      },
    ],
  },
  {
    id: POS_RAIL_EXIT_ID,
    railTitle: 'sidebar.posNav.backShort',
    railIcon: ADMIN_SHELL_NAV_ICONS.posExit,
    name: 'POS Salir',
    posModeOnly: true,
    children: [
      {
        displayName: 'sidebar.posNav.backToMilinco',
        iconName: ADMIN_SHELL_NAV_ICONS.posExit,
        route: '/pos/salir',
      },
    ],
  },
];

function filterNavChildrenForAccess(
  children: NavItem[],
  canManageTeam: boolean,
  can?: PermissionCheckFn,
  modules?: StorefrontModuleGate,
  rolesAdmin?: boolean,
  plan?: PlanModuleGate,
  hideMyPageChecklist?: boolean,
  hidePosTurnos?: boolean
): NavItem[] {
  const result: NavItem[] = [];
  let index = 0;

  while (index < children.length) {
    const item = children[index];
    if (item.navCap) {
      const cap = item;
      const sectionItems: NavItem[] = [];
      index += 1;
      while (index < children.length && !children[index].navCap) {
        sectionItems.push(children[index]);
        index += 1;
      }
      if (cap.teamAdminOnly && !canManageTeam) {
        continue;
      }
      const visibleSection = sectionItems
        .filter((child) => {
          if (child.teamAdminOnly && !canManageTeam) {
            return false;
          }
          if (child.route?.startsWith('/account/roles') && rolesAdmin === false) {
            return false;
          }
          if (child.route) {
            return childRouteVisible(
              child.route,
              can,
              modules,
              rolesAdmin,
              plan,
              hideMyPageChecklist,
              hidePosTurnos
            );
          }
          return true;
        })
        .map((child) => {
          if (!child.route) {
            return child;
          }
          // Nav operativo: sin chip Plan; rutas fuera de plan ya filtradas arriba.
          return child;
        });
      if (visibleSection.length > 0) {
        result.push(cap);
        result.push(...visibleSection);
      }
      continue;
    }

    if (item.teamAdminOnly && !canManageTeam) {
      index += 1;
      continue;
    }
    if (item.route?.startsWith('/account/roles') && rolesAdmin === false) {
      index += 1;
      continue;
    }
    if (
      item.route &&
      !childRouteVisible(
        item.route,
        can,
        modules,
        rolesAdmin,
        plan,
        hideMyPageChecklist,
        hidePosTurnos
      )
    ) {
      index += 1;
      continue;
    }
    result.push(item);
    index += 1;
  }

  return result;
}

function modulesForRolesAndMode(
  roles: string[],
  mode: AdminShellMode,
  options: NavBuildOptions = {}
): readonly NavModuleConfig[] {
  const isSuperadmin = rolesIncludeSuperadmin(roles);
  const canManageTeam = rolesCanManageTeamAccess(roles, options.can);
  const plan = options.planModules;

  if (mode === 'pos') {
    return NAV_MODULES.filter(
      (m) => m.posModeOnly && modulePassesPermission(m.id, roles, options.can)
    )
      .filter((m) => railModuleVisibleForPlan(m.id, plan))
      .filter((m) => !(options.hidePosTurnos && m.id === POS_RAIL_TURNOS_ID));
  }

  const base = (() => {
    if (!isSuperadmin) {
      return NAV_MODULES.filter(
        (m) =>
          !m.superadminOnly &&
          !m.posModeOnly &&
          (!m.teamAdminOnly || canManageTeam) &&
          modulePassesPermission(m.id, roles, options.can)
      );
    }
    if (mode === 'platform') {
      return NAV_MODULES.filter((m) => m.superadminOnly);
    }
    return NAV_MODULES.filter(
      (m) =>
        !m.superadminOnly &&
        !m.posModeOnly &&
        (!m.teamAdminOnly || canManageTeam) &&
        modulePassesPermission(m.id, roles, options.can)
    );
  })();

  return base.filter((m) => railModuleVisibleForPlan(m.id, plan));
}

/** Menú lateral según roles, permisos efectivos y modo de shell (tenant vs plataforma). */
export function buildNavItemsForRoles(
  roles: string[],
  mode: AdminShellMode = 'tenant',
  options: NavBuildOptions = {}
): NavItem[] {
  const canManageTeam = rolesCanManageTeamAccess(roles, options.can);
  const modules = effectiveStorefrontModules(options.storefrontModules, options.planModules);
  return modulesForRolesAndMode(roles, mode, options)
    .map((m) => ({
      id: m.id,
      name: m.name,
      children: filterNavChildrenForAccess(
        m.children,
        canManageTeam,
        options.can,
        modules,
        options.rolesAdmin,
        options.planModules,
        options.hideMyPageChecklist,
        options.hidePosTurnos
      ),
    }))
    .filter((m) => (m.children?.length ?? 0) > 0);
}

function shouldExposeConfigModuleInRail(
  module: NavModuleConfig,
  modulesWithVisibleChildren: readonly NavModuleConfig[]
): boolean {
  if (module.id !== CONFIG_NAV_MODULE_ID || !module.railHidden) {
    return false;
  }
  const otherRailModules = modulesWithVisibleChildren.filter(
    (candidate) => candidate.id !== CONFIG_NAV_MODULE_ID && !candidate.railHidden
  );
  return otherRailModules.length === 0;
}

function moduleVisibleInRail(
  module: NavModuleConfig,
  modulesWithVisibleChildren: readonly NavModuleConfig[]
): boolean {
  if (!module.railHidden) {
    return true;
  }
  return shouldExposeConfigModuleInRail(module, modulesWithVisibleChildren);
}

/** Rail vertical según roles, permisos y modo de shell (sin módulos `railHidden`). */
export function buildMenuItemsForRoles(
  roles: string[],
  mode: AdminShellMode = 'tenant',
  options: NavBuildOptions = {}
): { id: number; title: string; iconName: string }[] {
  const canManageTeam = rolesCanManageTeamAccess(roles, options.can);
  const modules = effectiveStorefrontModules(options.storefrontModules, options.planModules);
  const plan = options.planModules;
  const modulesWithVisibleChildren = modulesForRolesAndMode(roles, mode, options).filter(
    (m) =>
      filterNavChildrenForAccess(
        m.children,
        canManageTeam,
        options.can,
        modules,
        options.rolesAdmin,
        plan,
        options.hideMyPageChecklist,
        options.hidePosTurnos
      ).length > 0
  );

  return modulesWithVisibleChildren
    .filter((m) => moduleVisibleInRail(m, modulesWithVisibleChildren))
    .map((m) => ({
      id: m.id,
      title: m.railTitle,
      iconName: m.railIcon,
    }));
}

export function isProfileUtilityUrl(url: string): boolean {
  const path = url.split('?')[0];
  return path.startsWith('/account/academia') || path.startsWith('/account/gemas');
}

/** Home tenant (`/dashboard`). */
export function isTenantDashboardHomeUrl(url: string): boolean {
  const path = url.split('?')[0];
  return path === '/dashboard' || path.startsWith('/dashboard/');
}

/** Panel lateral solo si el módulo tiene subnavegación real (≥2 rutas). */
export function moduleHasSubnav(items: NavItem[], moduleId: number | null | undefined): boolean {
  if (moduleId == null) {
    return false;
  }
  return routesForModule(items, moduleId).length > 1;
}

export function isConfigurationUrl(url: string): boolean {
  const path = url.split('?')[0];
  // Utilidades del menú de perfil: no abren el panel lateral de Configuración.
  if (isProfileUtilityUrl(path)) {
    return false;
  }
  return path.startsWith('/settings') || path.startsWith('/account');
}

export const navItems: NavItem[] = buildNavItemsForRoles([], 'tenant');

/** Ítems del rail vertical (`app-iconmenu`): deben mantener el mismo `id` que cada grupo en `navItems`. */
export const menuItems: { id: number; title: string; iconName: string }[] = buildMenuItemsForRoles(
  [],
  'tenant'
);

/** Menú horizontal: un ítem por módulo del rail; ≥2 rutas → dropdown. */
export function buildHorizontalNavItems(
  items: NavItem[],
  railItems: readonly { id: number; title: string; iconName: string }[]
): NavItem[] {
  const railById = new Map(railItems.map((rail) => [rail.id, rail]));
  const result: NavItem[] = [];

  for (const group of items) {
    if (group.id == null || !railById.has(group.id)) {
      continue;
    }
    const routes = (group.children ?? []).filter(
      (child): child is NavItem =>
        !!(child.route && child.displayName) && child.mobileDockOnly !== true
    );
    if (routes.length === 0) {
      continue;
    }

    const rail = railById.get(group.id)!;
    if (routes.length === 1) {
      result.push({
        ...routes[0],
        id: group.id,
        displayName: rail.title,
        iconName: rail.iconName || routes[0].iconName,
      });
      continue;
    }

    const hubRoute =
      defaultTenantRouteForModule(items, group.id) ??
      defaultRouteForModule(items, group.id) ??
      routes[0].route;

    result.push({
      id: group.id,
      displayName: rail.title,
      iconName: rail.iconName || routes[0].iconName,
      route: hubRoute ?? undefined,
      children: routes.map((route) => ({ ...route, subItemIcon: true })),
    });
  }

  return result;
}

/** @deprecated Preferir `buildHorizontalNavItems(navItems, menuItems)`. */
export const horizontalNavItems: NavItem[] = buildHorizontalNavItems(navItems, menuItems);

export function findNavModuleIdForUrl(items: NavItem[], url: string): number | null {
  const path = url.split('?')[0];
  for (const menu of items) {
    const mid = menu.id;
    if (mid == null || !menu.children) {
      continue;
    }
    for (const child of menu.children) {
      const r = child.route;
      if (!r) {
        continue;
      }
      if (path === r || path.startsWith(`${r}/`)) {
        return mid;
      }
    }
  }
  return null;
}

export function defaultRouteForModule(
  items: NavItem[],
  moduleId: number
): string | null {
  const routes = routesForModule(items, moduleId);
  return routes[0] ?? null;
}

/**
 * Primera ruta hija del módulo en orden de menú (igual que Finanzas, Inventario, …).
 * Omite «Abrir POS» (`openPosInNewTab` / `/pos/*` en pestaña nueva).
 * El POS embebido (`/sales/pos/*`) sí es destino válido (p. ej. Ventas → Venta).
 */
export function defaultTenantRouteForModule(
  items: NavItem[],
  moduleId: number
): string | null {
  const module = items.find((item) => item.id === moduleId);
  if (!module?.children?.length) {
    return null;
  }
  for (const child of module.children) {
    if (!child.route?.trim() || child.openPosInNewTab || child.disabled) {
      continue;
    }
    const path = child.route.startsWith('/') ? child.route : `/${child.route}`;
    if (path === '/pos' || path.startsWith('/pos/')) {
      continue;
    }
    return path;
  }
  return null;
}

/** Rutas hijas navegables de un módulo (orden del menú). */
export function routesForModule(items: NavItem[], moduleId: number): string[] {
  const module = items.find((item) => item.id === moduleId);
  if (!module?.children?.length) {
    return [];
  }
  const routes: string[] = [];
  for (const child of module.children) {
    if (child.route?.trim()) {
      routes.push(child.route.startsWith('/') ? child.route : `/${child.route}`);
    }
  }
  return routes;
}

/** Selecciona el módulo activo del sidebar cuando el rail o la URL no coinciden con `navItems`.
 * Solo valida/elige un id presente en el nav actual; no inventa highlight.
 * El highlight del rail debe seguir a `FullComponent.selectedIcon` + URL (ver DEVELOPMENT.md «Admin shell rail sync»).
 */
export function resolveSelectedNavModuleId(
  navItems: readonly NavItem[],
  railMenuItems: readonly { id: number }[],
  currentModuleId: number | null | undefined
): number | null {
  const navModuleIds = navItems
    .map((item) => item.id)
    .filter((id): id is number => id != null);
  if (navModuleIds.length === 0) {
    return null;
  }

  // `null` es estado válido (utilidades de perfil / sin módulo activo); no inventar highlight.
  if (currentModuleId == null) {
    return null;
  }

  if (navModuleIds.includes(currentModuleId)) {
    return currentModuleId;
  }

  const railIds = railMenuItems.map((item) => item.id);
  if (railIds.length > 0) {
    return railIds[0] ?? null;
  }

  return navModuleIds[0] ?? null;
}
